import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientApi } from '../api';
import { useToast } from '../../../shared/ui/Toast/ToastProvider';
import { getErrorMessage } from '../../../shared/errors/getErrorMessage';

// Pipeline principal de operaciones (todo lo que no es la vista Cosmic/Telefónica -- ver
// useCosmicClients.js para esa). Reemplaza el `fetchOps` (rama no-cosmic) + `useState(ops)` que
// vivían en ClientsView.jsx.
//
// `useQuery` (React Query 5) ya no soporta `onError` -- el error se expone vía
// `query.error`/`query.isError` para que el container decida cómo mostrarlo (`policies/code.md`,
// alternativa explícita a un toast automático para queries de lectura). `useCreateOp`/
// `useUpdateOp` sí son mutaciones y sí soportan `onError` directo, ver abajo.
export function useClientOps({ category, range, q, enabled = true } = {}) {
    return useQuery({
        queryKey: ['client', 'ops', { category, range, q }],
        queryFn: async () => {
            const d = await clientApi.listOps({ category, range, q });
            return d.ops || [];
        },
        enabled,
    });
}

// `useCreateOp`/`useUpdateOp` vivían en useClientData.js sin invalidación -- todo caller
// compensaba llamando `fetchOps()` a mano (ClientsView.jsx: wizard submit, InlineSelect/
// InlineIndustryInput onSave, archivar, AddOpModal onSave). Movidos acá con invalidación real;
// esos call sites manuales se retiran en el mismo commit que wire este hook.
export function useCreateOp() {
    const qc = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: (payload) => clientApi.createOp(payload),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['client', 'ops'] }),
        onError: (e) => toast.error(getErrorMessage(e)),
    });
}

export function useUpdateOp() {
    const qc = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: ({ opId, fields }) => clientApi.updateOp(opId, fields),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['client', 'ops'] }),
        onError: (e) => toast.error(getErrorMessage(e)),
    });
}
